// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import { Box } from 'grommet'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { useTranslation, Trans } from 'react-i18next'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { isAuthzV2London, isMSP } from '../../../../../utils/feature-flag-utils'
import {
  Button,
  ModalDialog,
  ModalHeader,
  ModalFooter,
  Typography,
  FormInput,
  CCSForm
} from '../../../../../components'
import { del } from '../../../../../utils/api-utils'
import Markdown from '../../../../../commoncomponents/Markdown'

const DeleteRoleConfirmationModal = ({
  closeDeleteModal,
  role = {},
  setApiError
}) => {
  const navigate = useNavigate()
  const [isDisabled, setIsDisabled] = useState(false)
  const [deleteInputValue, setDeleteInputValue] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const { t } = useTranslation(['authz', 'common', 'iam'])

  const LDFlags = useFlags()

  const isDeleteConfirmed = () => {
    let result = false
    if (isEmpty(deleteInputValue?.trim())) {
      setErrorMsg(t('common:this_is_required'))
    } else if (
      deleteInputValue?.trim() &&
      deleteInputValue?.trim() !== t('common:delete_keyword')
    ) {
      setErrorMsg(t('common:invalid_keyword'))
    } else {
      setErrorMsg('')
      result = true
    }
    return result
  }
  const handleDeleteRole = async () => {
    if (isAuthzV2London(LDFlags) && !isDeleteConfirmed()) return
    try {
      setIsDisabled(true)
      const response = await del(`/authorization/v2alpha1/roles/${role.id}`, {})

      if (response.status === 204) {
        closeDeleteModal()
        navigate('/manage-account/identity/roles', {
          state: {
            roleDeleteNotification: {
              title: t('iam:roles.delete_role_success_title'),
              message: role?.displayName,
              severity: 'info'
            }
          }
        })
      } else {
        closeDeleteModal()
        setApiError(response.statusText)
        setIsDisabled(false)
      }
    } catch (error) {
      closeDeleteModal()
      setApiError(error)
    }
  }
  const deleteDialogContentLondon = () => {
    const content = (
      <Box>
        {isMSP() ? (
          <Typography
            margin={{ vertical: 'small' }}
            type="text"
            testId="msp-role-delete-desc"
          >
            <Trans>
              {t('iam:msp.delete_role_desc_line1_v2', {
                role: t('common:business_object.role'),
                roleName: role?.displayName,
                workspace: t('common:business_object.wkspc'),
                workspaces: t('common:business_object.wkspc_plural')
              })}
            </Trans>
            <Markdown>
              {t('iam:msp.delete_role_desc_line2', {
                roleAssignments: t(
                  'common:business_object.role_assignment_plural'
                ),
                role: t('common:business_object.role'),
                workspaces: t('common:business_object.wkspc_plural')
              })}
            </Markdown>
            <Markdown>
              {t('iam:msp.delete_role_desc_line3', {
                roleAssignment: t('common:business_object.role_assignment'),
                workspace: t('common:business_object.wkspc')
              })}
            </Markdown>
          </Typography>
        ) : (
          <Typography
            margin={{ vertical: 'small' }}
            type="text"
            testId="role-delete-desc"
          >
            <Trans>
              {t('iam:roles.delete_role_desc_line1_v2', {
                role: t('common:business_object.role'),
                roleName: role?.displayName,
                roleAssignment: t('common:business_object.role_assignment'),
                roleAssignments: t(
                  'common:business_object.role_assignment_plural'
                ),
                user: t('common:business_object.user'),
                workspace: t('common:business_object.wkspc')
              })}
            </Trans>
            <Markdown>
              {t('iam:roles.delete_role_desc_line2', {
                roleAssignments: t(
                  'common:business_object.role_assignment_plural'
                ),
                role: t('common:business_object.role')
              })}
            </Markdown>
          </Typography>
        )}

        <CCSForm errorMessage="" testId="confirm-delete-form">
          <FormInput
            required
            name="deleteInputValue"
            inputType="text"
            label={t('common:confirmation')}
            labelHelper={t('iam:msp.delete_role_confirmation', {
              DELETE: t('common:delete_keyword')
            })}
            value={deleteInputValue}
            testId="delete-role-confirmation"
            error={errorMsg}
            onChange={(e) => {
              setDeleteInputValue(e?.target?.value)
              setErrorMsg('')
            }}
          />
        </CCSForm>
      </Box>
    )

    return isEmpty(role) ? null : content
  }

  const deleteDialogContentKobe = isEmpty(role) ? null : (
    <Typography type="text" margin={{ top: 'medium' }}>
      <Trans i18nKey="authz:roles.del_role_confirmation" t={t}>
        <b>{{ role: role?.displayName }}</b>
      </Trans>
    </Typography>
  )

  return (
    <ModalDialog
      onClickOutside={() => {
        closeDeleteModal()
      }}
      header={
        <ModalHeader
          title={
            <Typography
              level="2"
              style={{ whiteSpace: 'nowrap' }}
              testId="header-title"
              type="heading"
              weight="bold"
            >
              {t('authz:roles.delete_role')}
            </Typography>
          }
        />
      }
      content={
        isAuthzV2London(LDFlags)
          ? deleteDialogContentLondon()
          : deleteDialogContentKobe
      }
      onClose={closeDeleteModal}
      testId="center-modal-dialog"
      footer={
        <ModalFooter
          right={
            <Box direction="row" gap="medium">
              <Button
                label={<b>{t('common:cancel')}</b>}
                testId="secondary-btn"
                onClick={closeDeleteModal}
              />
              <Button
                label={t('authz:roles.delete_role')}
                primary
                testId="primary-btn"
                onClick={handleDeleteRole}
                disabled={isDisabled}
              />
            </Box>
          }
        />
      }
    />
  )
}

DeleteRoleConfirmationModal.propTypes = {
  closeDeleteModal: PropTypes.func.isRequired,
  role: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired
  }),
  setApiError: PropTypes.func.isRequired
}

export { DeleteRoleConfirmationModal }
